import { EnfinityPrimaryBtn } from "@/components/design-system/enfinity/EnfinityButton";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ArrowRight } from "lucide-react";

export default function OurServices() {
  const { ref, isVisible } = useScrollReveal();

  const services = [
    {
      title: "Get paid on your terms",
      highlightedText: "your terms",
      highlightColor: "text-[#00d115]",
      description: "Enfinity makes it simple to withdraw your income on your preferred schedule, giving you full control over how and when you get paid.",
      image: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/get-paid.png",
      type: "payment",
      link: "https://enfinipay.com/",
    },
    {
      title: "Earn up to 30% to 100% more content from UGC claims with no extra work or cost",
      highlightedText: "Earn up to 30% to 100% more content",
      highlightColor: "text-[#9c44e9]",
      description: "Enfinity is a leading YouTube Copyright Partner that has UGC monetization down to a science. We turn fan-uploads and unauthorized reuploads into profitable revenue streams for you. The best part? No work or cost is required on your part.",
      descriptionHighlight: "a leading YouTube Copyright Partner",
      descriptionHighlightColor: "text-[#9c44e9]",
      image: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/protect-your-content.png",
      mobileImage: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/protect-your-content.png",
      imageClassName: "w-full h-full object-contain max-w-[85%] max-h-[85%] object-bottom scale-[1.3] mb-[30px] lg:mb-[33px] xxs:mb-[9px] xxs:max-w-[70%] xxs:max-h-[70%] xxs:scale-[2.2] sm:scale-[2] xxs:mb-[31px] xxs:ml-6 lg:ml-0 lg:max-w-[70%] lg:max-h-[70%] lg:scale-[1.4]",
      type: "shield",
      useOutline: true,
      link: "https://enfinishield.com/",
    },
    {
      title: "Premium, exclusive distribution opportunities",
      highlightedText: "Premium, exclusive",
      highlightColor: "text-[#3B82F6]",
      description: "Earn beyond your primary platform. We have negotiated premium content partnerships with industry titans like Microsoft, YouTube, Snap Starts, and more. We even have our in-house community of promotional pages, putting your content in front of over 2.9 billion new eyes each month.",
      images: "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/grow-beyond.png",
      type: "boost",
      link: "https://enfiniboost.com/",
    },
    {
      title: "Scale your output without burning out",
      highlightedText: "without burning out",
      highlightColor: "text-[#9c44e9]",
      description: "Kick burnout’s butt with EnfiniTask, our solution to expensive, complicated hiring and creator’s frustration. Creators are sick of wearing the “HR” hat, so we allow them to put it aside.",
      image: null,
      type: "task",
      link: "https://enfinitask.com/",
    },
  ];

  return (
    <section
      id="OurServices"
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } py-20 lg:py-24 px-4 xxs:px-6 xs:px-10 sm:px-10 md:px-12 lg:px-6 xl:px-8 bg-white`}
    >
      <div className="mx-auto max-w-7xl">
        {/* Header Section */}
        <div className="mb-10 lg:mb-58">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-8">
            <div className="flex-1">
              <p className="text-[#757575] text-xs xxs:text-xs xs:text-sm sm:text-sm md:text-md lg:text-lg font-medium mb-6 4xl:text-[24px] enfinity-subtext-font xxs:text-center xs:text-center sm:text-center md:text-center lg:text-left enfinity-subtext-font">
              What are we known for
              </p>
              
              <h2 className="text-2xl xxs:text-[55px] lg:text-[70px] 4xl:text-[90px] font-normal text-black mb-6 leading-tight xxs:leading-[1.3] xs:leading-[1.3] sm:leading-[1.2] md:leading-[1.2] enfinity-header-font xxs:text-center xs:text-center sm:text-center md:text-center lg:text-left enfinity-header-font">
              <span className="bg-gradient-to-r from-[#00d115] via-[#00b0ff] to-[#9c44e9] bg-clip-text text-transparent enfinity-header-font">
              Tools and services <br />
                </span>
                that compliment your <span className="xl:block">creator journey</span>
                
                
              </h2>
              <p className="text-lg md:text-xl text-black font-normal enfinity-subtext-font xxs:text-center xs:text-center sm:text-center md:text-center lg:text-left xxs:text-[1rem]">
                We do not interfere with your channel operations, brand deals, or strategy.
              </p>
            </div>
            <div className="flex flex-col gap-6 xxs:pt-0 xs:pt-0 sm:pt-0 md:pt-0 lg:pt-[65px] xxs:items-center xs:items-center sm:items-center md:items-center lg:items-start">
              <a
                href="https://core.enfinity.com/signup"
                target="_blank"
                rel="noopener noreferrer"
                className="w-fit xxs:self-center xs:self-center sm:self-center md:self-center lg:self-end"
              >
                <EnfinityPrimaryBtn
                  className="flex items-center gap-2 w-fit"
                >
                  <span className="enfinity-subtext-font">Get started with us</span>
                  <ArrowRight className="w-5 h-5" />
                </EnfinityPrimaryBtn>
              </a>
              
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
          {services.map((service, index) => {
            // Define column spans for each card: Row 1 = 40% 60%, Row 2 = 60% 40%
            const getColSpan = () => {
              if (index === 0) return "lg:col-span-4"; // Row 1, Card 1 - 40%
              if (index === 1) return "lg:col-span-6"; // Row 1, Card 2 - 60%
              if (index === 2) return "lg:col-span-6"; // Row 2, Card 3 - 60%
              return "lg:col-span-4"; // Row 2, Card 4 - 40%
            };
            
            const CardWrapper = service.link ? "a" : "div";
            const wrapperProps = service.link
              ? { href: service.link, target: "_blank", rel: "noopener noreferrer" }
              : {};
            return (
            <CardWrapper
              key={index}
              className={`relative rounded-[20px] overflow-hidden bg-white border border-[#ddd] shadow-[0_4px_6px_rgba(0,0,0,0.1)] min-h-[380px] h-[380px] xxs:h-[410px] lg:min-h-[420px] lg:h-[470px] block transition-all duration-300 ease-out hover:scale-[1.02] hover:shadow-lg ${service.link ? "cursor-pointer" : ""} ${getColSpan()}`}
              {...wrapperProps}
            >
              {service.type === "payment" && (
                <div className="absolute top-6 right-6 z-20">
                  {/* <img
                    src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/powered.svg"
                    alt="Powered by"
                    className="h-5 w-auto object-contain"
                  /> */}
                </div>
              )}

              <div className="relative z-10 h-full flex flex-col">
                <div className="p-8">
                  <h3 className="text-3xl xxs:text-lg lg:text-[25px] 4xl:text-3xl font-normal text-black mb-4 enfinity-subtext-font">
                  {service.title.split(service.highlightedText)[0]}
                  <span className={`${service.highlightColor} enfinity-subtext-font`}>
                    {service.highlightedText}
                  </span>
                  {service.title.split(service.highlightedText)[1]}
                </h3>
                <p className="text-black text-[16px] xxs:text-sm lg:text-[16px] 4xl:text-[16px] mb-4 enfinity-subtext-font">
                  {service.descriptionHighlight ? (
                    <>
                      {service.description.split(service.descriptionHighlight)[0]}
                      <span className={service.descriptionHighlightColor || "text-[#9c44e9]"}>
                        {service.descriptionHighlight}
                      </span>
                      {service.description.split(service.descriptionHighlight)[1]}
                    </>
                  ) : (
                    service.description
                  )}
                </p>
                {service.type === "payment" && (
                  <div className="flex flex-wrap items-center gap-0">
                    {/* <img
                      src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/compatible.svg"
                      alt="Compatible platforms"
                      className="h-7 w-auto object-contain"
                    /> */}
                  </div>
                )}
                </div>

                {/* Image/Illustration Area */}
                <div className="relative flex-1 bg-transparent rounded-xl overflow-visible">
                  {service.type === "payment" && (
                    <div className="absolute inset-0 flex items-start justify-center xxs:-mt-[-12px] xs:-mt-[-40px] sm:-mt-[-28px] md:-mt-[50px] lg:-mt-[-40px] xl:-mt-[-40px] 1xl:-mt-[-25px] 2xl:-mt-[-24px] 4xl:-mt-[-20px]">
                      {/* Notification Card with Shadow */}
                      <img
                        src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/get-paid.png"
                        alt="Pay"
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  )}

                  {service.type === "shield" && service.image && (
                    <div className="absolute inset-0 flex items-end justify-center pb-0">
                      {service.mobileImage ? (
                        <>
                          <img
                            src={service.mobileImage}
                            alt={service.title}
                            className={`lg:hidden ${service.imageClassName || "w-full h-full object-contain max-w-[120%] max-h-[120%] object-bottom"}`}
                          />
                          <img
                            src={service.image}
                            alt={service.title}
                            className={`hidden lg:block ${service.imageClassName || "w-full h-full object-contain max-w-[120%] max-h-[120%] object-bottom"}`}
                          />
                        </>
                      ) : (
                        <img
                          src={service.image}
                          alt={service.title}
                          className={service.imageClassName || "w-full h-full object-contain max-w-[120%] max-h-[120%] object-bottom"}
                        />
                      )}
                    </div>
                  )}

                  {service.type === "boost" && (
                    <div className="inset-0 flex flex-col items-center justify-end">
                      <img
                        src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/grow-beyond.png"
                        alt={service.title}
                        className="xxs:max-w-[200px] xs:max-w-[240px] sm:max-w-[260px] md:max-w-[105%] lg:max-w-md xxs:h-[120%] xs:h-[115%] sm:h-[110%] md:h-[105%] lg:h-full object-contain -mt-[30px] xxs:-mt-[20px] xs:-mt-[30px] sm:-mt-[30px] md:-mt-[40px] lg:-mt-[-20px] 4xl:-mt-[-15px]"
                      />
                      <div className="flex items-center justify-between w-full px-[32px] mt-auto pt-[60px] pb-2 xxs:mt-[-10px] sm:mt-[-20px] lg:mt-[-13px] 4xl:mt-[-15px]">
                        {/* <img
                          src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/p-boost.svg"
                          alt="Powered by"
                          className="lg:h-5 xxs:h-3 w-auto object-contain "
                        /> */}
                        {/* <img
                          src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/learn-more.svg"
                          alt="Learn more"
                          className="lg:h-5 xxs:h-3 w-auto object-contain"
                        /> */}
                      </div>
                    </div>
                  )}

                  {service.type === "task" && ( 
                    <div className="absolute inset-0 flex items-end justify-center pb-0">
                      <img
                        src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/desktop.png"
                        alt={service.title}
                        className="w-full h-full object-contain object-bottom max-w-[90%] max-h-[90%]"
                      />
                    </div>
                  )}

                </div>
              </div>
            </CardWrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
}
